import { ViewEncapsulation, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Component, AfterViewInit, OnDestroy } from '@angular/core';
import { Marker, Map } from 'mapbox-gl';
import { Feature } from 'geojson';

import { MoteEventType, MoteEvent, FeatureOptions, Coords } from '../../models';
import { JourneyPoint, JourneyPointType, JourneyPointFeature, JourneyType } from '../../components/transportation/transportation.model';

// From local motes SDK
import { MapService, MoteService, IconService } from '../../providers';
import { AutoSubOnDestroy, AutoSub } from '../../providers/auto-sub';
import { MoteEventService } from '../../providers/mote-event.service';
import { MapClickEvent } from '../../providers/map.service';
import { ProjectService } from '../../providers/project.service';

@AutoSubOnDestroy()
@Component({
    selector: 'feature-map-marker',
    templateUrl: './feature-map-marker.component.html',
    styleUrls: ['./feature-map-marker.component.scss'],
    encapsulation: ViewEncapsulation.None, // Set to None as we cant contain this in component selector because only part of the dom is inserted as mapbox marker
    changeDetection: ChangeDetectionStrategy.Default
})
export class FeatureMapMarkerComponent implements AfterViewInit, OnDestroy {

    @Output() moteEvent = new EventEmitter<MoteEvent>();

    @ViewChild('featureMarkerContainer', { static: true }) markerContainer: ElementRef;

    public selectedPoint: JourneyPoint = null;
    public isOpen = false;
    public markerDiv: HTMLElement;
    public JourneyPointType = JourneyPointType;

    private map: Map;
    private mapboxMarker = null;

    constructor(private mapService: MapService, private moteEventService: MoteEventService, private moteService: MoteService, private iconService: IconService, private projectService: ProjectService) {
    }

    ngAfterViewInit() {
        // Wait for map to init
        this.mapService.awaitInit().then(map => {
            this.map = map;
            this.markerDiv = this.markerContainer.nativeElement;
            this.initListners(this.mapService);
            // Debug, print to console to easier access in dom
            // console.log(this.markerContainer.nativeElement);
        });
    }

    // Make initListners public for debug purposes
    public initListners(mapService: MapService) {
        // Watch map clicks
        AutoSub(this).reg['clickSub'] = mapService.watchMapClick().subscribe(async ev => {
            // Do nothing if we are not in preview mode
            if(!mapService.previewModeEnabled) return;
            this.mapClick(ev);
        });

        AutoSub(this).reg['releaseSub'] = mapService.watchMapRelease().subscribe(ev => {
            // Do nothing if we are not in preview mode
            if(!this.mapService.previewModeEnabled) return;
            this.mapRelease(ev);
        });

        AutoSub(this).reg['eventSub'] = this.moteEventService.watchMoteEvents().subscribe((ev: MoteEvent) => {

            switch(ev.type) {
                case MoteEventType.SHOW_MOTE_ON_MAP:
                case MoteEventType.SWIPER_MOTE_FOCUS:
                case MoteEventType.SHOW_MAP_MARKER:
                case MoteEventType.MAP_MOTE_SELECTED:
                case MoteEventType.HIDE_FEATURE_ON_MAP:
                    // Hide map marker
                    this.isOpen = false;
                    break;

                case MoteEventType.SHOW_FEATURE_ON_MAP:
                    this.isOpen = false;
                    // Extract feature id from event
                    let opt = ev.options as FeatureOptions;
                    if(!opt.featureId) return console.warn("Got invalid mote feature ev ", ev);
                    // Lookup the feature data
                    let feature = this.mapService.getPointFeature(opt.featureId);
                    // Show map marker
                    this.openMarker(feature, 400).then(() => {
                        this.isOpen = true;
                    }).catch(() => { });
                    break;
            }
        });
    }

    public mapClick(ev: MapClickEvent) {
        // Do not open here, as we need to detect map drag before opening feature
    }

    public mapRelease(ev: MapClickEvent) {
        // Close all other markers (if it wasent a move click)
        if(!ev.mapMoved && ev.features.length <= 0) {
            // The map or mote map marker will emit this event, so no need to duplicate unselect event
            // this.emitMoteEvent(MoteEventType.MAP_MOTE_UNSELECTED);
            this.isOpen = false;
        }

        if(ev.mapMoved) return;
        // Clear open flag
        this.isOpen = false;

        // Open the closest feature
        this.openMarker(ev.features[0]).then((feature) => {
            this.emitMoteEvent(MoteEventType.MAP_FEATURE_SELECTED);
            this.isOpen = true;
        }).catch(() => { });
    }

    private openMarker(feature: Feature, delay = 30): Promise<JourneyPoint> {
        // Do nothing for cluster features
        if(!feature) return Promise.reject(null);
        if(feature.properties.cluster) return Promise.reject(null);

        return new Promise(async (resolve, reject) => {
            // Ignore this if clicked feature is not a journey point type 
            if(!feature.properties.pointType) {
                return reject(null);
            }

            let point = feature as JourneyPointFeature;
            let journeyPoint = new JourneyPoint(
                point.properties.name,
                point.properties.label,
                point.properties.place,
                new Coords(point.geometry.coordinates[1], point.geometry.coordinates[0]),
                point.properties.pointType,
                point.properties.journeyType);

            if(!feature.geometry) return console.log("Invalid feature selected");
            this.addMapboxMarker(journeyPoint.coords);
            // Select feature
            this.selectedPoint = journeyPoint;

            // Delay open a bit for transition to be recognized
            setTimeout(() => resolve(journeyPoint), delay);
        });
    }

    private emitMoteEvent(type: MoteEventType) {
        this.moteEvent.emit({ mote: null, type: type });
    }

    /**
    *  Add mapbox marker if not already
    */
    private addMapboxMarker(position: Coords) {
        if(!position) return;
        if(!this.mapService.initialized) return;

        if(this.mapboxMarker) {
            this.mapboxMarker.remove();
        }
        this.mapboxMarker = new Marker(this.markerDiv, { offset: [0, 0] });
        this.mapboxMarker.setLngLat(position.longLat()).addTo(this.map);
    }

    public ngOnDestroy() {
        if(this.mapboxMarker) this.mapboxMarker.remove();
    }
}
